/* LoginStream — the operator's live view of a server-hosted OnlyFans login.
 *
 * A TS/React port of the proven streamed-login-agent operator canvas. Opens the
 * relay WebSocket (bigdayz.web_console proxies it to the login agent, ADR 0001),
 * renders CDP screencast frames onto a <canvas>, and forwards the operator's
 * mouse/keyboard as input messages. The operator drives a real OnlyFans login
 * (incl. 2FA) they never installed anything for; on "Done" the agent captures
 * the header bundle and submits it to the Session Keeper.
 *
 * Reused by AddAccountFlow (first connect) and the Change-proxy re-login.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../../api/client';
import { C } from './onboardingKit';

// Standard 16:9. Must stay in lockstep with the agent's pinned viewport
// (streamed-login-agent relayServer.js) so operator clicks map 1:1 onto the
// real page.
const VIEWPORT_W = 1280;
const VIEWPORT_H = 720;

type StatusCode =
  | 'opening-browser'
  | 'login-required'
  | 'ready-to-capture'
  | 'connecting'
  | 'connected'
  | 'at-capacity'
  | 'session-expired'
  | 'stopped'
  | 'cancelled'
  | 'error'
  | 'idle';

// Deliberately minimal: no text that narrates *how* the connection works
// (opening a browser, capturing a session, the login host, …). Only an
// actionable readiness hint, terminal outcomes, and errors get a line.
const STATUS_LABEL: Record<StatusCode, string> = {
  idle: '',
  'opening-browser': '',
  'login-required': '',
  'ready-to-capture': "Looks logged in — press Done when you're on your account.",
  connecting: 'One moment…',
  connected: 'Connected!',
  'at-capacity': 'Busy right now — please try again shortly.',
  'session-expired': 'Timed out — please start again.',
  stopped: 'Login stopped.',
  cancelled: 'Login cancelled.',
  error: 'Something went wrong — please try again.',
};

const SPECIAL_KEYS: Record<string, { code: string; vk: number; text?: string }> = {
  Backspace: { code: 'Backspace', vk: 8 },
  Enter: { code: 'Enter', vk: 13 },
  Tab: { code: 'Tab', vk: 9 },
  ' ': { code: 'Space', vk: 32, text: ' ' },
};

// Touch devices: a <canvas> can't summon the on-screen keyboard, so a tap on the
// stream focuses a hidden textarea instead. Mobile keyboards report most
// keydowns as "Unidentified", so we read its `input` events and diff the value
// against this sentinel (non-empty so Backspace still produces an edit).
const KBD_SENTINEL = '_'.repeat(32);

interface LoginStreamProps {
  accountId: string;
  /** Fired when the agent reports a successful capture + submission. */
  onConnected: () => void;
  /** Fired on a terminal failure/timeout (operator can retry). */
  onError?: (code: StatusCode) => void;
  /** Fired after the operator presses Stop and the login is torn down. */
  onStopped?: () => void;
  /** Which session to log in: primary (slot 1, default) or a REST-only booster. */
  role?: 'primary' | 'booster';
  /** Pin a specific booster slot (2-3) when re-logging a dead one. */
  slot?: number;
  /**
   * Booster-only: which proxy this login egresses through. Sent as the first WS
   * frame (not the URL — a manual proxy carries credentials). Ignored for primary.
   *   same   — the account's current proxy (default)
   *   auto   — a pool proxy provisioned server-side
   *   manual — the operator-supplied `proxyUrl`
   */
  boosterProxy?: { mode: 'same' | 'auto' | 'manual'; proxyUrl?: string };
}

export function LoginStream({ accountId, onConnected, onError, onStopped, role, slot, boosterProxy }: LoginStreamProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const lastMoveRef = useRef(0);
  const kbdRef = useRef<HTMLTextAreaElement | null>(null);
  const kbdPrevRef = useRef(KBD_SENTINEL);
  const composingRef = useRef(false);
  const [status, setStatus] = useState<StatusCode>('idle');
  const [ready, setReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Parents pass fresh callbacks/objects each render; reading them through refs
  // keeps those re-renders from tearing down and reopening the stream socket.
  const onConnectedRef = useRef(onConnected);
  const onErrorRef = useRef(onError);
  const boosterProxyRef = useRef(boosterProxy);
  onConnectedRef.current = onConnected;
  onErrorRef.current = onError;
  boosterProxyRef.current = boosterProxy;

  const send = useCallback((obj: unknown) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
  }, []);

  // Forward one logical key (a printable char or a SPECIAL_KEYS name).
  const sendKey = useCallback((key: string) => {
    const s = SPECIAL_KEYS[key];
    if (!s) {
      if (key.length === 1) send({ type: 'input', event: { kind: 'key', type: 'char', text: key, key } });
      return;
    }
    if (s.text) {
      send({ type: 'input', event: { kind: 'key', type: 'char', text: s.text, key } });
      return;
    }
    send({ type: 'input', event: { kind: 'key', type: 'rawKeyDown', key, code: s.code, windowsVirtualKeyCode: s.vk } });
    send({ type: 'input', event: { kind: 'key', type: 'keyUp', key, code: s.code, windowsVirtualKeyCode: s.vk } });
  }, [send]);

  // Operator pressed Stop: tell the agent to cancel (it tears down Chrome), then
  // close the socket so the stream halts immediately on our side too — closing
  // also makes the backend release the provisional proxy claim. Settle the UI on
  // a terminal 'cancelled' state and hand control back to the parent flow.
  const stop = useCallback(() => {
    setStatus('cancelled');
    setReady(false);
    const ws = wsRef.current;
    try {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'cancel' }));
    } catch { /* socket already going away */ }
    try {
      ws?.close();
    } catch { /* already closing */ }
    onStopped?.();
  }, [onStopped]);

  // --- WebSocket lifecycle ---
  useEffect(() => {
    const img = new Image();
    imgRef.current = img;
    img.onload = () => {
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx) ctx.drawImage(img, 0, 0, VIEWPORT_W, VIEWPORT_H);
    };

    const ws = new WebSocket(api.loginStreamUrl(accountId, { role, slot }));
    wsRef.current = ws;

    ws.onopen = () => {
      // For a booster, the very first frame is the proxy choice — the backend
      // reads exactly one message before wiring up the login (kept off the URL
      // because a manual proxy carries credentials). Primary logins send nothing.
      if (role === 'booster') {
        const cfg = boosterProxyRef.current ?? { mode: 'same' as const };
        ws.send(JSON.stringify({ type: 'proxyConfig', mode: cfg.mode, proxyUrl: cfg.proxyUrl }));
      }
    };

    ws.onmessage = (e) => {
      let msg: { type?: string; code?: StatusCode; data?: string; message?: string };
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }
      if (msg.type === 'frame' && msg.data) {
        img.src = `data:image/jpeg;base64,${msg.data}`;
      } else if (msg.type === 'status' && msg.code) {
        setStatus(msg.code);
        if (msg.code === 'ready-to-capture') setReady(true);
        if (msg.code === 'at-capacity' || msg.code === 'session-expired') onErrorRef.current?.(msg.code);
      } else if (msg.type === 'done') {
        setStatus('connected');
        onConnectedRef.current();
      } else if (msg.type === 'error') {
        // A server-supplied message (e.g. no distinct proxy to auto-provision)
        // replaces the generic error line so the operator knows what to fix.
        if (msg.message) setErrorMsg(msg.message);
        setStatus('error');
        onErrorRef.current?.('error');
      }
    };
    ws.onclose = () => {
      setStatus((s) => (s === 'connected' ? s : s === 'idle' ? 'error' : s));
    };
    ws.onerror = () => setStatus('error');

    return () => {
      try {
        ws.close();
      } catch {
        /* already closing */
      }
      wsRef.current = null;
    };
  }, [accountId, role, slot]);

  // --- coordinate mapping: canvas element px -> page CSS px ---
  const toPage = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    const r = ev.currentTarget.getBoundingClientRect();
    return {
      x: Math.round((ev.clientX - r.left) * (VIEWPORT_W / r.width)),
      y: Math.round((ev.clientY - r.top) * (VIEWPORT_H / r.height)),
    };
  };

  const onMouseMove = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    const now = Date.now();
    if (now - lastMoveRef.current < 40) return; // throttle
    lastMoveRef.current = now;
    const { x, y } = toPage(ev);
    send({ type: 'input', event: { kind: 'mouse', type: 'mouseMoved', x, y, buttons: ev.buttons } });
  };
  const onMouseDown = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    ev.preventDefault();
    const { x, y } = toPage(ev);
    send({ type: 'input', event: { kind: 'mouse', type: 'mousePressed', x, y, button: 'left', buttons: 1, clickCount: 1 } });
  };
  const onMouseUp = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    const { x, y } = toPage(ev);
    send({ type: 'input', event: { kind: 'mouse', type: 'mouseReleased', x, y, button: 'left', buttons: 0, clickCount: 1 } });
  };
  // Must run inside the tap gesture or iOS won't open the keyboard. The
  // synthetic mousedown that follows calls preventDefault, so focus sticks.
  const onPointerUp = (ev: React.PointerEvent<HTMLCanvasElement>) => {
    if (ev.pointerType === 'mouse') return;
    kbdRef.current?.focus({ preventScroll: true });
  };

  // Hidden-textarea input → keystrokes: drop the common prefix/suffix, Backspace
  // over what was removed, type what was added. Mid-composition (IME/predictive
  // text) we only track the value; resetting it then would break the IME.
  const flushKbd = () => {
    const el = kbdRef.current;
    if (!el) return;
    const prev = kbdPrevRef.current;
    const next = el.value;
    let p = 0;
    while (p < prev.length && p < next.length && prev[p] === next[p]) p++;
    let q = 0;
    while (q < prev.length - p && q < next.length - p && prev[prev.length - 1 - q] === next[next.length - 1 - q]) q++;
    for (let i = p; i < prev.length - q; i++) sendKey('Backspace');
    for (const ch of next.slice(p, next.length - q)) sendKey(ch === '\n' ? 'Enter' : ch);
    if (composingRef.current) {
      kbdPrevRef.current = next;
      return;
    }
    el.value = KBD_SENTINEL;
    el.setSelectionRange(KBD_SENTINEL.length, KBD_SENTINEL.length);
    kbdPrevRef.current = KBD_SENTINEL;
  };

  // Keyboard: capture on window so it works regardless of canvas focus. Only
  // active while streaming; released on unmount.
  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      // Ignore when typing into an actual input on our page (none today, but safe).
      const tag = (ev.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (ev.metaKey || ev.ctrlKey) {
        // Undo (Cmd/Ctrl+Z, without Shift) → forward as an OS-independent edit
        // command; the agent runs it on the remote page regardless of its OS.
        if ((ev.key === 'z' || ev.key === 'Z') && !ev.shiftKey) {
          ev.preventDefault();
          send({ type: 'input', event: { kind: 'edit', command: 'undo' } });
          return;
        }
        // Other clipboard/OS shortcuts (Cmd/Ctrl+V/C/A/X…) resolve locally
        // rather than being mis-forwarded as a literal character. Don't
        // preventDefault so the browser's native `paste` event still fires —
        // the paste listener below relays the pasted text to the remote page.
        return;
      }
      ev.preventDefault();
      sendKey(ev.key);
    };
    // Paste: the operator's clipboard lives in *their* browser, not the remote
    // Chrome, so a Cmd/Ctrl+V keystroke alone can't paste there. Read the local
    // clipboard text and replay it as individual char events onto the page.
    const onPaste = (ev: ClipboardEvent) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const tag = (ev.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const text = ev.clipboardData?.getData('text');
      if (!text) return;
      ev.preventDefault();
      for (const ch of text) {
        send({ type: 'input', event: { kind: 'key', type: 'char', text: ch, key: ch } });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('paste', onPaste);
    };
  }, [send, sendKey]);

  const terminal =
    status === 'connected' || status === 'error' || status === 'at-capacity' ||
    status === 'session-expired' || status === 'cancelled' || status === 'stopped';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ color: C.muted, fontSize: 14.5, minHeight: 20 }}>
        {status === 'error' && errorMsg ? errorMsg : STATUS_LABEL[status]}
      </div>
      <div
        style={{
          borderRadius: 14,
          overflow: 'hidden',
          border: `1px solid ${C.line}`,
          background: '#000',
          lineHeight: 0,
          aspectRatio: `${VIEWPORT_W} / ${VIEWPORT_H}`,
          position: 'relative',
        }}
      >
        <textarea
          ref={kbdRef}
          defaultValue={KBD_SENTINEL}
          aria-hidden
          tabIndex={-1}
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          onFocus={(ev) => ev.currentTarget.setSelectionRange(KBD_SENTINEL.length, KBD_SENTINEL.length)}
          onInput={flushKbd}
          onCompositionStart={() => { composingRef.current = true; }}
          onCompositionEnd={() => { composingRef.current = false; flushKbd(); }}
          onKeyDown={(ev) => {
            if (ev.key === 'Tab') {
              ev.preventDefault();
              sendKey('Tab');
            }
          }}
          // 16px font stops iOS from zooming the page on focus.
          style={{
            position: 'absolute', top: 0, left: 0, width: 1, height: 1, padding: 0, border: 0,
            opacity: 0, fontSize: 16, resize: 'none', pointerEvents: 'none',
          }}
        />
        <canvas
          ref={canvasRef}
          width={VIEWPORT_W}
          height={VIEWPORT_H}
          onMouseMove={onMouseMove}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onPointerUp={onPointerUp}
          style={{ display: 'block', width: '100%', height: '100%', cursor: 'crosshair', background: '#000' }}
        />
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <button
          className="om-btn"
          disabled={!ready || terminal}
          onClick={() => send({ type: 'capture' })}
          style={{
            fontFamily: 'inherit', fontWeight: 700, fontSize: 15, borderRadius: 12, padding: '12px 22px',
            border: 'none', cursor: ready && !terminal ? 'pointer' : 'default',
            background: ready && !terminal ? C.purpleHi : 'color-mix(in srgb, var(--om-accent) 25%, transparent)',
            color: '#fff', opacity: ready && !terminal ? 1 : 0.6,
          }}
        >
          Done
        </button>
        <button
          className="om-btn"
          disabled={terminal}
          onClick={stop}
          style={{
            fontFamily: 'inherit', fontWeight: 700, fontSize: 15, borderRadius: 12, padding: '12px 22px',
            border: `1px solid ${C.line2}`, cursor: terminal ? 'default' : 'pointer',
            background: 'transparent', color: C.muted, opacity: terminal ? 0.6 : 1,
          }}
        >
          Stop
        </button>
      </div>
    </div>
  );
}
